import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Toggle from '@cloudscape-design/components/toggle';
import { useWebPush } from '@/lib/useWebPush';

/**
 * Card "Notificações" da tela de Configurações do console super-admin:
 * liga/desliga as notificações web push (FCM) deste navegador. Opt-out — vem
 * ligado por padrão; ao ligar pela primeira vez o navegador pede a permissão.
 * O registro/renovação do token acontece em `<WebPushBootstrap />`; aqui é só
 * o controle. Quem envia push para super-admins é `notifyAdmins`
 * (`apps/functions/src/lib/adminNotify.ts`), filtrado pelas preferências por
 * evento de `NotificationPrefsCard`.
 *
 * Origem: sem issue — commit 7994b1111, 2026-05-12 20:54 +0200 (web push no admin).
 *
 * Usado por: `routes/SettingsPage.tsx`.
 *
 * Histórico:
 *  - 2026-10-10 13:39 +0200 — (#926) removida a nota "hoje nenhum evento faz
 *    fan-out para super-admins", falsa desde 6d8cac847 (2026-06-19, `notifyAdmins`
 *    com push por uid).
 */
export function NotificationsCard(): JSX.Element {
  const { t } = useTranslation();
  const { loading, available, unavailableReason, permission, on, busy, setEnabled } = useWebPush();

  return (
    <Container
      header={
        <Header variant="h2" description={t('admin.settings.notifications.description')}>
          {t('admin.settings.notifications.title')}
        </Header>
      }
    >
      {loading ? (
        <Box color="text-body-secondary">{t('admin.settings.notifications.loading')}</Box>
      ) : !available ? (
        <Box color="text-body-secondary">
          {unavailableReason === 'unconfigured'
            ? t('admin.settings.notifications.unavailable')
            : t('admin.settings.notifications.unsupported')}
        </Box>
      ) : (
        <SpaceBetween size="xs">
          <Toggle
            checked={on}
            disabled={busy || permission === 'denied'}
            onChange={({ detail }): void => {
              void setEnabled(detail.checked);
            }}
          >
            {t('admin.settings.notifications.toggle')}
          </Toggle>
          {permission === 'denied' ? (
            <Box fontSize="body-s" color="text-status-inactive">
              {t('admin.settings.notifications.blockedHint')}
            </Box>
          ) : null}
        </SpaceBetween>
      )}
    </Container>
  );
}
